import * as Phaser from "phaser-ce"
import { CCUtils } from "../../common/CCUtils"
import { log } from "../../common/Logging"
import { colorNum } from "./ColorUtils"
import { PhaserUtils } from "./PhaserUtils"
import { Scale } from "./Scale"

export interface Movable {
    x: number
    y: number
    width: number
    height: number
}

type SpriteAsset = string | Phaser.RenderTexture | Phaser.BitmapData | PIXI.Texture

export class Sprites {

    /**
     * Debug can help place sprites by making the draggable and self printing their coors
     */
    static add = (
        game: Phaser.Game, asset?: SpriteAsset,
        {x = 0, y = 0, scale = 1, alpha = 1, group, debug = false}:
            { x?: number, y?: number, scale?: number, alpha?: number, group?: Phaser.Group, debug?: boolean } = {}
    ) => {
        const sprite = new Phaser.Sprite(game, x, y, asset)
        Scale.scaleSprite(sprite, scale)
        sprite.alpha = alpha
        if (group) {
            group.add(sprite)
        } else {
            game.add.existing(sprite)
        }

        if (debug) {
            Sprites.enableDebug(sprite)
        }

        return sprite
    }

    static enableDebug = (sprite: Phaser.Sprite) => {
        if (CCUtils.debug) {
            sprite.inputEnabled = true
            sprite.input.enableDrag()
            sprite.events.onDragStop.add(() => {
                log.debug(`${sprite.key} is at\n${PhaserUtils.coors(sprite.x, sprite.y, 1000)}\nCenter is at\n${PhaserUtils.coors(sprite.centerX, sprite.centerY, 1000)}`)
            })
        }
    }

    static playAnimationNTimes = (sprite: Phaser.Sprite, animationName: string, times: number, onComplete?: () => void) => {
        let played = 0
        const previousAnim = sprite.animations.play(animationName)
        previousAnim.onComplete.add(() => {
            played++
            if (played >= times) {
                log.debug(`Done playing animation ${animationName} ${times} times. Played total: ${played}`)
                if (onComplete) {
                    previousAnim.onComplete.removeAll()
                    onComplete()
                }
            } else {
                log.debug(`Playing animation ${animationName} again. Played so far ${played}`)
                sprite.animations.play(animationName)
            }
        })
    }

    static fadeOut = (fade: Phaser.Sprite | Phaser.Image, fadeDuration = 500): Phaser.Tween => {
        return Sprites.fade(fade, {fadeIn: false, fadeDuration})
    }

    static fade = (
        fade: Phaser.Sprite | Phaser.Image | Phaser.Text,
        {
            fadeIn = true, fadeDuration = 500, fadeAmount = null, destroy = false, onComplete = undefined
        }: { fadeIn?: boolean, fadeDuration?: number, fadeAmount?: number, destroy?: boolean, onComplete?: () => void } = {}
    ): Phaser.Tween => {

        const endAlpha = fadeAmount ? fadeAmount : fadeIn ? 1 : 0
        const tween = fade.game.add.tween(fade).to({alpha: endAlpha}, fadeDuration, Phaser.Easing.Linear.None, true)
        tween.onComplete.addOnce(() => {
            if (onComplete) {
                onComplete()
            }
            if (destroy) {
                fade.destroy()
            }
        })
        return tween
    }

    static fadeInAndOut = (
        fade: Phaser.Sprite | Phaser.Image,
        {
            fadeIn = true, fadeDuration = 500, fadeAmount = null
        }: { fadeIn?: boolean, fadeDuration?: number, fadeAmount?: number } = {}
    ) => {
        Sprites.fade(fade, {fadeIn, fadeDuration, fadeAmount, onComplete: () => Sprites.fadeInAndOut(fade, {fadeIn: !fadeIn, fadeDuration, fadeAmount})})
    }

    static fadeGroup = (
        fadeAll: Phaser.Group,
        {
            fadeIn = true, fadeDuration = 500, fadeAmount = null, destroy = false, onComplete = undefined
        }: { fadeIn?: boolean, fadeDuration?: number, fadeAmount?: number, destroy?: boolean, onComplete?: () => void } = {}
    ) => {
        let idx = 0
        fadeAll.forEach((fade) => {
            Sprites.fade(fade, {fadeIn, fadeDuration, fadeAmount, destroy, onComplete: idx === 0 ? onComplete : undefined})
            idx++
        })
    }

    static centerCoorForSpriteInSprite = (centerInCoor: number, centerInSize: number, toCenterSize: number) => {
        return Math.floor((centerInCoor + centerInSize / 2) - (toCenterSize * 0.5))
    }

    static centerSpriteInSprite = (
        centerIn: Movable,
        center: Movable,
        centerText = false,
        xOnly = false
    ) => {
        center.x = Sprites.centerCoorForSpriteInSprite(centerIn.x, centerIn.width, center.width)
        if (!xOnly) {
            center.y = Sprites.centerCoorForSpriteInSprite(centerIn.y, centerIn.height, center.height)
        }
        if (centerText) {
            // Text sprites look too high when "centered" in terms of pixels, and instanceof doesn't work.
            center.y += Scale.y(0.005)
        }
    }

    static centerTextInSprite = (centerIn: Movable, center: Movable, centerText = true, xOnly = false) => {
        Sprites.centerSpriteInSprite(centerIn, center, centerText, xOnly)
    }

    static reversedSpriteFrames = (framesWide: number, framesHigh: number, frames: number = null): Array<number> => {
        if (!frames) {
            frames = framesWide * framesHigh
        }
        const reversed = []

        let totalFrames = 0
        for (let row = 0; row < framesHigh; row++) {
            for (let currentFrame = framesWide * (row + 1); currentFrame > row * framesWide; currentFrame--) {
                totalFrames++
                reversed.push(currentFrame - 1)
                if (totalFrames === frames) {
                    break
                }
            }
        }

        return reversed
    }

    static centerInWorld = (sprite: Movable) => {
        Sprites.centerInWorldXOnly(sprite)
        sprite.y = Math.floor((Scale.availableHeight() / 2) - (sprite.height / 2))
    }

    /**
     * Works full screen or partial screen.
     */
    static centerInWorldXOnly = (sprite: Movable) => {
        sprite.x = Math.floor((Scale.availableWidth() / 2) - (sprite.width / 2))
    }

    static toLeftOf = (place: Movable, toLeftOf: Movable, padding = 0.04) =>
        place.x = toLeftOf.x - Scale.x(padding) - place.width

    static toRightOf = (toRightOf: Movable, padding = 0.04): number => {
        return Math.floor(toRightOf.x + toRightOf.width + Scale.width() * padding)
    }

    static alignWithScreenBottom = (toAlign: Movable) => toAlign.y = Scale.y() - toAlign.height

    static evenlySpaceHorizontally = (within: Movable, first: Movable, second: Movable) => {
        const spaceWidth = (within.width - first.width - second.width) / 3
        log.debug(`Within width: ${within.width} firstWidth: ${first.width} secondWidth: ${second.width} spaceWidth: ${spaceWidth}`)
        first.x = within.x + spaceWidth
        second.x = first.x + first.width + spaceWidth
    }

    static evenlySpaceHorizontallyBetween = (start: number, end: number, ...space: Array<Movable>) => {
        const spaces = space.length - 1
        const spaceWidth = (end - start) / spaces
        for (let x = 0; x < space.length; x++) {
            const item = space[x]
            log.debug("x for thing " + (start - item.width / 2 + x * spaceWidth))
            item.x = start - item.width / 2 + x * spaceWidth
        }
    }

    static evenlySpaceVertically = (within: Movable, first: Movable, second: Movable) => {
        const spaceHeight = (within.height - first.height - second.height) / 3
        log.debug(`Within height: ${within.height} firstHeight: ${first.height} secondHeight: ${second.height} spaceHeight: ${spaceHeight}`)
        first.y = within.y + spaceHeight
        second.y = first.y + first.height + spaceHeight
    }

    static rightAlign = (alighWith: Movable, ...align: Array<Movable>) => {
        align.forEach((toAlign) => toAlign.x = alighWith.x + alighWith.width - toAlign.width)
    }
    static bottomAlign = (alignWith: Movable, ...align: Array<Movable>) => {
        align.forEach(toAlign => toAlign.y = alignWith.y + alignWith.height - toAlign.height)
    }
    static leftAlign = (alighWith: Movable, ...align: Array<Movable>) => {
        align.forEach((toAlign) => toAlign.x = alighWith.x)
    }
    static topAlign = (alignWith: Movable, ...align: Array<Movable>) => {
        align.forEach(toAlign => toAlign.y = alignWith.y)
    }

    static checkOverlap = (spriteA: Phaser.Sprite, spriteB: Phaser.Sprite) => {
        const boundsA = spriteA.getBounds() as Phaser.Rectangle
        const boundsB = spriteB.getBounds() as Phaser.Rectangle

        return Phaser.Rectangle.intersects(boundsA, boundsB)
    }

    static grayOut = (sprite: Phaser.Sprite) => sprite.tint = colorNum.gray

    static undoGrayOut = (sprite: Phaser.Sprite) => sprite.tint = colorNum.white
}
